import { useCallback, useRef, useState } from 'react';

interface HistoryState<T> {
    past: T[];
    present: T;
    future: T[];
}

interface SetStateOptions {
    forceSnapshot?: boolean;
}

const MAX_HISTORY = 50; // Giới hạn 50 snapshots
const DEBOUNCE_MS = 500; // Đợi 500ms không có thay đổi mới chụp snapshot
const LARGE_DELETION_THRESHOLD = 10; // Xóa >10 ký tự = snapshot ngay

/**
 * Detect large content changes that should trigger immediate snapshot
 * Returns true if user is deleting significant content
 */
function detectLargeChange<T>(oldValue: T, newValue: T): boolean {
    // Chỉ detect cho string content
    if (typeof oldValue !== 'string' || typeof newValue !== 'string') {
        return false;
    }

    const oldLength = oldValue.length;
    const newLength = newValue.length;
    const deleted = oldLength - newLength;

    // Nếu xóa nhiều hơn THRESHOLD → cần snapshot before-state
    return deleted > LARGE_DELETION_THRESHOLD;
}

export function useHistory<T>(initialState: T) {
    // ✅ FIX #1: Snapshot initial state ngay lập tức
    const [history, setHistory] = useState<HistoryState<T>>({
        past: [initialState], // ← Lưu initial state vào past
        present: initialState,
        future: []
    });

    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const lastValueRef = useRef<T>(initialState); // ← Track giá trị trước đó

    const setState = useCallback((value: T, options?: SetStateOptions) => {
        const { forceSnapshot = false } = options || {};

        // ✅ FIX #2: Detect large deletion và snapshot before-state
        const shouldForceSnapshot = forceSnapshot || detectLargeChange(lastValueRef.current, value);

        if (shouldForceSnapshot) {
            // Chụp snapshot ngay lập tức
            setHistory(prev => {
                const newPast = [...prev.past, prev.present];
                // Giới hạn past array
                if (newPast.length > MAX_HISTORY) {
                    newPast.shift(); // Xóa snapshot cũ nhất
                }
                return {
                    past: newPast,
                    present: value,
                    future: [] // Clear future khi có thay đổi mới
                };
            });
            lastValueRef.current = value;
        } else {
            // Debounced snapshot
            // 🐛 FIX: phải chụp lại giá trị TRƯỚC khi đổi (lastValueRef.current) ngay bây giờ,
            // vì present sẽ bị ghi đè bằng value ngay dưới đây. Snapshot cũ đọc `prev.present`
            // BÊN TRONG setTimeout — nhưng lúc đó present đã là `value` (giá trị MỚI) rồi, nên
            // push nhầm bản sao của giá trị mới vào past thay vì giá trị cũ. Hậu quả: lần Undo
            // đầu tiên không có tác dụng (past chứa bản sao present), lần Undo thứ hai nhảy lùi
            // quá xa, xóa mất nhiều đợt gõ trước đó (mất nội dung note).
            const valueBeforeThisChange = lastValueRef.current;

            // Cập nhật present ngay lập tức (để UI responsive)
            setHistory(prev => ({
                ...prev,
                present: value
            }));

            // Hủy timer cũ
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            // Đặt timer mới để chụp snapshot sau DEBOUNCE_MS
            debounceTimerRef.current = setTimeout(() => {
                setHistory(prev => {
                    // Kiểm tra xem value có khác present không (tránh snapshot trùng)
                    if (prev.present === value) {
                        const newPast = [...prev.past, valueBeforeThisChange];
                        if (newPast.length > MAX_HISTORY) {
                            newPast.shift();
                        }
                        lastValueRef.current = value;
                        return {
                            past: newPast,
                            present: value,
                            future: []
                        };
                    }
                    return prev;
                });
            }, DEBOUNCE_MS);
        }
    }, []);

    const undo = useCallback(() => {
        setHistory(prev => {
            if (prev.past.length === 0) return prev;
            const previous = prev.past[prev.past.length - 1];
            const newPast = prev.past.slice(0, -1);

            // Sync ref to restored state
            lastValueRef.current = previous;

            return {
                past: newPast,
                present: previous,
                future: [prev.present, ...prev.future]
            };
        });
    }, []);

    const redo = useCallback(() => {
        setHistory(prev => {
            if (prev.future.length === 0) return prev;
            const next = prev.future[0];
            const newFuture = prev.future.slice(1);

            // Sync ref to restored state
            lastValueRef.current = next;

            return {
                past: [...prev.past, prev.present],
                present: next,
                future: newFuture
            };
        });
    }, []);

    const clear = useCallback(() => {
        // Clear debounce timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        setHistory(prev => ({
            past: [],
            present: prev.present,
            future: []
        }));
    }, []);

    const canUndo = history.past.length > 0;
    const canRedo = history.future.length > 0;

    return {
        state: history.present,
        setState,
        undo,
        redo,
        canUndo,
        canRedo,
        clear
    };
}
